import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from 'react'
import {useTranslation} from 'react-i18next'
import {DateTime} from 'luxon'
import {
  toTimeString,
  isValidTimeHHmm,
  isTimeOrderValid,
  isRangeInsideAny,
  doHhMmRangesOverlap,
} from '@common/utils/dates'
import type {
  TimeRangeResponse,
  AssignedSlotRangeResponse,
  DayAggregatedRangesResponse,
  PractitionerRangesResponse,
} from '@common/models'

import StaffAssignmentDayRangesModal from './StaffAssignmentDayRangesModal'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {Button} from '~/components/general/button/Button'
import {DatePicker} from '~/components/general/datePicker/DatePicker'
import {SimpleTable} from '~/components/general/table/SimpleTable'
import {
  useGetPartnerServices,
  useGetAggregatedDayTimeRanges,
  useGetDayTimeRangesForDay,
  useGetPractitionerAvailability,
  useReplaceSlotsForDay,
} from '~/api'
import {useGetPartnerAdminPractitioners} from '~/api/partnerAdminPractitioners'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {PartnerAdministratorContext} from '~/components/profile/PartnerAdministratorContext'

type TimeRange = {startTime: string; endTime: string}

export const StaffAssignmentPage: React.FC = () => {
  const {t} = useTranslation<string>()
  const {addNotification} = useContext(NotificationContext)
  const {partner} = useContext(PartnerAdministratorContext)
  const {data: services} = useGetPartnerServices()
  const [selectedServiceId, setSelectedServiceId] = useState<string>('')

  const {data: aggregated} = useGetAggregatedDayTimeRanges(
    selectedServiceId || undefined,
  )

  const {data: practitioners} = useGetPartnerAdminPractitioners()

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorDate, setEditorDate] = useState<Date | null>(null)
  const [editorPractitionerId, setEditorPractitionerId] = useState('')
  const [ranges, setRanges] = useState<TimeRange[]>([])
  const [touched, setTouched] = useState<Array<{start: boolean; end: boolean}>>(
    [],
  )

  // Time zone handling: use partner timeZone when available via partner data
  const partnerTimeZone = useMemo(
    () => partner?.timeZone || DateTime.local().zoneName,
    [partner],
  )

  const formatDateWithWeekday = useCallback(
    (isoDate: string) => {
      const dt = DateTime.fromISO(isoDate).setZone(partnerTimeZone)
      const weekday = dt.toLocaleString({weekday: 'long'})
      const date = dt.toLocaleString(DateTime.DATE_SHORT)
      return `${weekday} ${date}`
    },
    [partnerTimeZone],
  )

  // Helper: normalize any backend value to strict HH:mm
  const asHhMm = useCallback(
    (
      v: TimeRangeResponse['startTime'] | TimeRangeResponse['endTime'] | string,
    ): string => {
      if (typeof v === 'string' && v.includes('T')) {
        const dt = DateTime.fromISO(v)
        return dt.isValid ? dt.toFormat('HH:mm') : toTimeString(v)
      }
      return toTimeString(v)
    },
    [],
  )

  const isoDate = useMemo(
    () => (editorDate ? DateTime.fromJSDate(editorDate).toISODate() || '' : ''),
    [editorDate],
  )

  const {data: dayDetail, refetch: refetchDayDetail} =
    useGetDayTimeRangesForDay(
      {
        partnerServiceId: selectedServiceId,
        practitionerId: editorPractitionerId,
        date: isoDate,
      },
      {enabled: !!selectedServiceId && !!editorPractitionerId && !!isoDate},
    )

  const {data: availability} = useGetPractitionerAvailability(
    {practitionerId: editorPractitionerId, date: isoDate},
    {enabled: !!editorPractitionerId && !!isoDate},
  )

  const {mutate: replaceDay, isPending: isSaving} = useReplaceSlotsForDay(
    selectedServiceId,
    editorPractitionerId && isoDate
      ? {practitionerId: editorPractitionerId, date: isoDate}
      : undefined,
  )

  const serviceOptions = useMemo(
    () => (services || []).map((s) => ({label: s.name, value: s.id})),
    [services],
  )

  // Removed unused practitionerOptions JSX options

  const selectedPractitionerName = useMemo(() => {
    const p = (practitioners || []).find((x) => x.id === editorPractitionerId)
    return p ? `${p.firstName} ${p.secondName}`.trim() : ''
  }, [practitioners, editorPractitionerId])

  const openAdd = async () => {
    if (!editorPractitionerId || !isoDate) return

    // Force refetch to ensure we have the latest data from backend
    // This prevents showing the alert based on stale cached data
    const {data: freshDayDetail} = await refetchDayDetail()

    // If there is already day info for the practitioner, alert and load for editing
    if (freshDayDetail && freshDayDetail.availableTimeRanges?.length > 0) {
      alert(
        t(
          'This day already has ranges for the selected practitioner. You can edit them but cannot create another.',
        ),
      )
      setRanges(
        freshDayDetail.availableTimeRanges.map((r) => ({
          startTime: asHhMm(r.startTime),
          endTime: asHhMm(r.endTime),
        })),
      )
      setTouched(
        freshDayDetail.availableTimeRanges.map(() => ({
          start: true,
          end: true,
        })),
      )
      setEditorOpen(true)
      return
    }
    // Prefill using availability results
    if (availability?.bookableTimeRanges?.length) {
      const next = availability.bookableTimeRanges.map((r) => ({
        startTime: asHhMm(r.startTime),
        endTime: asHhMm(r.endTime),
      }))
      setRanges(next)
      setTouched(next.map(() => ({start: true, end: true})))
    } else {
      setRanges([])
      setTouched([])
    }
    setEditorOpen(true)
  }

  const handleSave = () => {
    if (!selectedServiceId || !editorPractitionerId || !isoDate) return
    // block save if any invalid range
    // basic format/order checks
    const anyInvalidBasic = (ranges || []).some(
      (r) =>
        !isValidTimeHHmm(r.startTime) ||
        !isValidTimeHHmm(r.endTime) ||
        !isTimeOrderValid(r.startTime, r.endTime),
    )
    if (anyInvalidBasic) return
    // overlap checks: ranges [a,b) and [c,d) overlap if a < d && c < b
    for (let i = 0; i < ranges.length; i += 1) {
      for (let j = i + 1; j < ranges.length; j += 1) {
        const r1 = ranges[i]
        const r2 = ranges[j]
        if (
          doHhMmRangesOverlap(
            r1.startTime,
            r1.endTime,
            r2.startTime,
            r2.endTime,
          )
        ) {
          return
        }
      }
    }
    replaceDay(
      {
        practitionerId: editorPractitionerId,
        date: isoDate,
        timeRanges: ranges.map((r) => ({
          startTime: r.startTime,
          endTime: r.endTime,
        })),
      },
      {
        onSuccess: () => {
          setEditorOpen(false)
          setRanges([])
          setTouched([])
        },
        onError: (e: Error) => {
          // 422 message handling
          const message = e?.message || 'Validation error'
          // Extract stable reason code at the start e.g., REASON: Detail...
          const [reasonCode] = message.split(':')
          let localized = message
          if (reasonCode === 'INVALID_TIME_RANGE') {
            localized = t('Invalid time range')
          } else if (reasonCode === 'OUTSIDE_WORKING_HOURS') {
            localized = t(
              "Requested time ranges are outside practitioner's working hours",
            )
          } else if (reasonCode === 'OVERLAPS_TIME_OFF') {
            localized = t(
              "Requested time ranges overlap practitioner's time off",
            )
          } else if (reasonCode === 'NON_ALTERABLE_SLOTS_PRESENT') {
            localized = t('There are non-alterable slots present for this day')
          } else if (reasonCode === 'PRACTITIONER_NOT_FOUND') {
            localized = t('Practitioner not found')
          } else if (reasonCode === 'PARTNER_MISMATCH') {
            localized = t('Forbidden: partner mismatch')
          }
          addNotification(localized, NotificationType.Error)
        },
      },
    )
  }

  const headerData = useMemo(
    () => ({
      columns: [
        {title: t('Staff'), columnWidthPercent: 20},
        {title: t('Date'), columnWidthPercent: 25},
        {title: t('Hours'), columnWidthPercent: 45},
        {title: '', columnWidthPercent: 10},
      ],
    }),
    [t],
  )

  const rows = useMemo(() => {
    type Row = {
      staff: string
      date: string
      hours: string
      practitionerId?: string
      dateIso?: string
      allRanges?: TimeRange[]
    }
    const flatRows: Row[] = []
    ;(aggregated || []).forEach((d: DayAggregatedRangesResponse) => {
      ;(d.practitioners || []).forEach((p: PractitionerRangesResponse) => {
        const staff = (practitioners || []).find(
          (x) => x.id === p.practitionerId,
        )
        let name: string
        if (staff) {
          name = `${staff.firstName} ${staff.secondName}`.trim()
        } else {
          const firstName = p.practitionerFirstName || ''
          const secondName = p.practitionerSecondName || ''
          name = `${firstName} ${secondName}`.trim() || p.practitionerId
        }

        const dateText = formatDateWithWeekday(d.date)
        const rangesList: TimeRange[] = (p.timeRanges || []).map((r) => ({
          startTime: asHhMm(r.startTime),
          endTime: asHhMm(r.endTime),
        }))
        const rangeTexts: string[] = rangesList.map(
          (r) => `${r.startTime}–${r.endTime}`,
        )

        // Join all time ranges with " / " separator in a single row
        const hoursText = rangeTexts.length === 0 ? '-' : rangeTexts.join(' / ')

        flatRows.push({
          staff: name,
          date: dateText,
          hours: hoursText,
          practitionerId: p.practitionerId,
          dateIso: d.date,
          allRanges: rangesList,
        })
      })
    })

    // Create table rows with Edit button for each row
    const tableRows = [] as Array<{
      columns: string[]
      actionComponent?: React.ReactNode
    }>
    for (let i = 0; i < flatRows.length; i += 1) {
      tableRows.push({
        columns: [flatRows[i].staff, flatRows[i].date, flatRows[i].hours],
        actionComponent: (
          <Button
            size="xs"
            onClick={() => {
              if (flatRows[i].practitionerId && flatRows[i].dateIso) {
                setEditorPractitionerId(flatRows[i].practitionerId || '')
                setEditorDate(
                  DateTime.fromISO(flatRows[i].dateIso || '').toJSDate(),
                )
                if (flatRows[i].allRanges) {
                  setRanges(flatRows[i].allRanges || [])
                }
              }
              setEditorOpen(true)
            }}>
            {t('Edit')}
          </Button>
        ),
      })
    }
    return tableRows
  }, [aggregated, practitioners, formatDateWithWeekday, t, asHhMm])

  // When opening editor with practitioner/date set, sync ranges from detail endpoint
  useEffect(() => {
    if (!editorOpen) return
    const existing = dayDetail?.availableTimeRanges
    if (existing && existing.length > 0) {
      setRanges(
        existing.map((r) => ({
          startTime: asHhMm(r.startTime),
          endTime: asHhMm(r.endTime),
        })),
      )
      return
    }
    const suggested = availability?.bookableTimeRanges
    if (suggested && suggested.length > 0) {
      setRanges(
        suggested.map((r) => ({
          startTime: asHhMm(r.startTime),
          endTime: asHhMm(r.endTime),
        })),
      )
    }
  }, [editorOpen, dayDetail, availability, asHhMm])

  const availabilityText = useMemo(() => {
    const formatList = (list?: TimeRangeResponse[]) => {
      if (!list || list.length === 0) return t('none')
      const parts = list.map(
        (r) => `${toTimeString(r.startTime)}–${toTimeString(r.endTime)}`,
      )
      if (parts.length === 1 && parts[0] === '00:00–23:59')
        return t('Available all day')
      return parts.join(', ')
    }

    const formatTimeOff = (list?: TimeRangeResponse[]) => {
      if (!list || list.length === 0) return t('Off all day')

      // Build human-readable availability from ranges
      const parts = (list || []).map(
        (r) =>
          `${toTimeString(r.startTime)} ${t('to')} ${toTimeString(r.endTime)}`,
      )

      // Full day available
      if (
        list.length === 1 &&
        toTimeString(list[0].startTime) === '00:00' &&
        toTimeString(list[0].endTime) === '23:59'
      ) {
        return t('Available all day')
      }

      const joinWithAnd = (arr: string[]) => {
        if (arr.length <= 1) return arr[0] || ''
        if (arr.length === 2) return `${arr[0]} ${t('and')} ${arr[1]}`
        return `${arr.slice(0, -1).join(', ')} ${t('and')} ${
          arr[arr.length - 1]
        }`
      }

      return `${t('Available from')} ${joinWithAnd(parts)}`
    }

    const formatAssigned = (list?: AssignedSlotRangeResponse[]) => {
      if (!list || list.length === 0) return t('none')
      const parts = list.map((r) => {
        const range = `${toTimeString(r.startTime)}–${toTimeString(r.endTime)}`
        const code = r.serviceInternalCode
        return code ? `${range} (${code})` : range
      })
      return parts.join(', ')
    }
    return {
      timeOff: formatTimeOff(availability?.availabilityBasedOnTimeOff),
      partner: formatList(availability?.availabilityBasedOnPartnerWorkingHours),
      staff: formatList(availability?.availabilityBasedOnStaffWorkingHours),
      assigned: formatAssigned(
        availability?.availabilityBasedSlotAlreadyAssigned,
      ),
    }
  }, [availability, t])

  // Validation for time ranges
  const isValidTime = useCallback((v?: string) => isValidTimeHHmm(v), [])

  const rangeErrors = useMemo(
    () =>
      (ranges || []).map((r, i) => {
        const errs: {start?: string; end?: string} = {}
        const wasTouched = touched[i] || {start: false, end: false}
        const startOk = isValidTime(r.startTime)
        const endOk = isValidTime(r.endTime)
        if (wasTouched.start && !startOk) errs.start = t('Invalid time')
        if (wasTouched.end && !endOk) errs.end = t('Invalid time')
        if (
          wasTouched.start &&
          wasTouched.end &&
          startOk &&
          endOk &&
          !isTimeOrderValid(r.startTime, r.endTime)
        ) {
          const msg = t('Start time must be before end time')
          errs.start = errs.start || msg
          errs.end = errs.end || msg
        }
        // Overlap with any other entered range
        if (
          wasTouched.start &&
          wasTouched.end &&
          startOk &&
          endOk &&
          isTimeOrderValid(r.startTime, r.endTime)
        ) {
          const overlapsOther = (ranges || []).some(
            (o, j) =>
              j !== i &&
              isValidTime(o.startTime) &&
              isValidTime(o.endTime) &&
              isTimeOrderValid(o.startTime, o.endTime) &&
              doHhMmRangesOverlap(
                r.startTime,
                r.endTime,
                o.startTime,
                o.endTime,
              ),
          )
          if (overlapsOther) {
            const msg = t('Overlapping range')
            errs.start = errs.start || msg
            errs.end = errs.end || msg
          }
        }
        // Validate inclusion within staff working hours from availability
        const allowedStaff: Array<{start: string; end: string}> = (
          availability?.availabilityBasedOnStaffWorkingHours || []
        ).map((x) => ({
          start: toTimeString(x.startTime),
          end: toTimeString(x.endTime),
        }))
        if (
          wasTouched.start &&
          wasTouched.end &&
          startOk &&
          endOk &&
          isTimeOrderValid(r.startTime, r.endTime) &&
          allowedStaff.length > 0 &&
          !isRangeInsideAny(r.startTime, r.endTime, allowedStaff)
        ) {
          const msg = t('Outside staff working hours')
          errs.start = errs.start || msg
          errs.end = errs.end || msg
        }
        return errs
      }),
    [ranges, isValidTime, t, availability, touched],
  )

  const hasInvalidRanges = useMemo(() => {
    if (rangeErrors.some((e) => !!e.start || !!e.end)) return true
    // Additionally require staff-hours-based inclusion if availability present
    const allowedStaff: Array<{start: string; end: string}> = (
      availability?.availabilityBasedOnStaffWorkingHours || []
    ).map((x) => ({
      start: toTimeString(x.startTime),
      end: toTimeString(x.endTime),
    }))
    if (!allowedStaff.length) return false
    // If all ranges are inside staff hours, still check for overlaps among themselves
    const anyOutside = (ranges || []).some(
      (r) => !isRangeInsideAny(r.startTime, r.endTime, allowedStaff),
    )
    if (anyOutside) return true
    for (let i = 0; i < ranges.length; i += 1) {
      for (let j = i + 1; j < ranges.length; j += 1) {
        const r1 = ranges[i]
        const r2 = ranges[j]
        const startOk1 = isValidTimeHHmm(r1.startTime)
        const endOk1 = isValidTimeHHmm(r1.endTime)
        const startOk2 = isValidTimeHHmm(r2.startTime)
        const endOk2 = isValidTimeHHmm(r2.endTime)
        if (
          startOk1 &&
          endOk1 &&
          startOk2 &&
          endOk2 &&
          isTimeOrderValid(r1.startTime, r1.endTime) &&
          isTimeOrderValid(r2.startTime, r2.endTime)
        ) {
          const overlaps =
            r1.startTime < r2.endTime && r2.startTime < r1.endTime
          if (overlaps) return true
        }
      }
    }
    return false
  }, [rangeErrors, availability, ranges])

  const title = selectedServiceId
    ? (services || []).find((s) => s.id === selectedServiceId)?.name ||
      'Service'
    : 'Staff assignment'

  return (
    <Container flex direction="column" spacing={4}>
      <Container flex justify="space-between" align="center">
        <Typography size="lg" weight="medium">
          {title}
        </Typography>
        {!!selectedServiceId && (
          <Button size="sm" onClick={() => setSelectedServiceId('')}>
            {t('Back')}
          </Button>
        )}
      </Container>

      {/* If no service is selected, only show the service selector */}
      {!selectedServiceId ? (
        <Container direction="column" spacing={1}>
          <Typography>{t('Service')}</Typography>
          <FilteredSelect
            width={600}
            placeholder={t('Select service')}
            options={serviceOptions}
            initialValue={
              serviceOptions.find((o) => o.value === selectedServiceId)
                ?.label || ''
            }
            onChange={(value) => setSelectedServiceId((value as string) || '')}
          />
        </Container>
      ) : (
        <>
          <Container flex spacing={3} align="flex-end">
            <Container direction="column" spacing={1}>
              <Typography>{t('Staff')}</Typography>
              <FilteredSelect
                size="small"
                width={360}
                placeholder={t('Select staff')}
                options={(practitioners || []).map((p: any) => ({
                  label: `${p.firstName} ${p.secondName}`.trim(),
                  value: p.id,
                }))}
                initialValue={selectedPractitionerName}
                onChange={(value) =>
                  setEditorPractitionerId((value as string) || '')
                }
              />
            </Container>

            <Container direction="column" spacing={1}>
              <Typography>{t('Date')}</Typography>
              <DatePicker
                selected={editorDate}
                onChange={(d) => setEditorDate(d as Date)}
              />
            </Container>

            <Button
              size="sm"
              onClick={openAdd}
              disabled={!editorPractitionerId || !editorDate}>
              {t('Add')}
            </Button>
          </Container>

          <SimpleTable header={headerData} rows={rows} />
        </>
      )}

      {/* Extracted modal component */}
      <StaffAssignmentDayRangesModal
        isVisible={editorOpen}
        isoDate={isoDate}
        ranges={ranges}
        rangeErrors={rangeErrors}
        isSaving={isSaving}
        hasInvalidRanges={hasInvalidRanges}
        availabilityText={availabilityText}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        onChangeStart={(i, v) =>
          setRanges((prev) =>
            prev.map((x, idx) => (idx === i ? {...x, startTime: v} : x)),
          )
        }
        onChangeEnd={(i, v) =>
          setRanges((prev) =>
            prev.map((x, idx) => (idx === i ? {...x, endTime: v} : x)),
          )
        }
        onBlurStart={(i) =>
          setTouched((prev) => {
            const next = [...(prev || [])]
            next[i] = {
              ...(next[i] || {start: false, end: false}),
              start: true,
            }
            return next
          })
        }
        onBlurEnd={(i) =>
          setTouched((prev) => {
            const next = [...(prev || [])]
            next[i] = {
              ...(next[i] || {start: false, end: false}),
              end: true,
            }
            return next
          })
        }
        onRemoveRange={(i) =>
          setRanges((prev) => prev.filter((_, idx) => idx !== i))
        }
        onAddRange={() => {
          setRanges((prev) => [...prev, {startTime: '', endTime: ''}])
          setTouched((prev) => [...(prev || []), {start: false, end: false}])
        }}
      />
    </Container>
  )
}
